import { AbstractParseTreeVisitor } from 'antlr4ts/tree/AbstractParseTreeVisitor.js';
export class AstVisitor extends AbstractParseTreeVisitor {
    BINARY_OPERATOR_MAP = {
        '+': 'addition',
        '-': 'subtraction',
        '*': 'multiplication',
        '/': 'division',
        '&&': 'and',
        '||': 'or',
    };
    UNARY_OPERATOR_MAP = {
        '!': 'not',
        '+': 'positive',
        '-': 'negative',
    };
    COMPARISON_OPERATOR_MAP = {
        '=': 'eq',
        '!=': 'neq',
        '<': 'lt',
        '<=': 'lte',
        '>': 'gt',
        ">=": 'gte',
    };
    defaultResult() {
        throw new Error('AstVisitor: unhandled grammar rule — ensure all visitor methods are implemented.');
    }
    visitParse(ctx) {
        return this.visit(ctx.expression());
    }
    visitExpression(ctx) {
        return this.visit(ctx.logicalOrExpression());
    }
    visitLogicalOrExpression(ctx) {
        let current = this.visit(ctx.logicalAndExpression(0));
        const expressions = ctx.logicalAndExpression();
        for (let i = 1; i < expressions.length; i++) {
            current = {
                type: 'binary',
                operator: this.BINARY_OPERATOR_MAP['||'],
                leftHand: current,
                rightHand: this.visit(expressions[i]),
            };
        }
        return current;
    }
    visitLogicalAndExpression(ctx) {
        let current = this.visit(ctx.comparisonExpression(0));
        const expressions = ctx.comparisonExpression();
        for (let i = 1; i < expressions.length; i++) {
            current = {
                type: 'binary',
                operator: this.BINARY_OPERATOR_MAP['&&'],
                leftHand: current,
                rightHand: this.visit(expressions[i]),
            };
        }
        return current;
    }
    visitComparisonExpression(ctx) {
        const expressions = ctx.additiveExpression();
        const leftHand = this.visit(expressions[0]);
        const operatorToken = ctx.children
            ?.map((child) => child.text)
            .find((text) => text === '=' ||
            text === '!=' ||
            text === '<' ||
            text === '<=' ||
            text === '>' ||
            text === '>=');
        if (!operatorToken)
            return leftHand;
        return {
            type: 'comparison',
            operator: this.COMPARISON_OPERATOR_MAP[operatorToken],
            leftHand,
            rightHand: this.visit(expressions[1]),
        };
    }
    visitAdditiveExpression(ctx) {
        let current = this.visit(ctx.multiplicativeExpression(0));
        const expressions = ctx.multiplicativeExpression();
        const operators = ctx.children
            ?.map((child) => child.text)
            .filter((text) => text === '+' || text === '-') ?? [];
        for (let i = 1; i < expressions.length; i++) {
            current = {
                type: 'binary',
                operator: this.BINARY_OPERATOR_MAP[operators[i - 1]],
                leftHand: current,
                rightHand: this.visit(expressions[i]),
            };
        }
        return current;
    }
    visitMultiplicativeExpression(ctx) {
        let current = this.visit(ctx.unaryExpression(0));
        const expressions = ctx.unaryExpression();
        const operators = ctx.children
            ?.map((child) => child.text)
            .filter((text) => text === '*' || text === '/') ?? [];
        for (let i = 1; i < expressions.length; i++) {
            current = {
                type: 'binary',
                operator: this.BINARY_OPERATOR_MAP[operators[i - 1]],
                leftHand: current,
                rightHand: this.visit(expressions[i]),
            };
        }
        return current;
    }
    visitUnaryExpression(ctx) {
        let current = this.visit(ctx.primaryExpression());
        const operatorTokens = ctx.children
            ?.map((child) => child.text)
            .filter((text) => text === '!' || text === '+' || text === '-') ?? [];
        for (let i = operatorTokens.length - 1; i >= 0; i--) {
            current = {
                type: 'unary',
                operator: this.UNARY_OPERATOR_MAP[operatorTokens[i]],
                operand: current,
            };
        }
        return current;
    }
    visitPrimaryExpression(ctx) {
        if (ctx.atom())
            return this.visit(ctx.atom());
        if (ctx.term())
            return this.visit(ctx.term());
        if (ctx.number())
            return this.visit(ctx.number());
        if (ctx.parenthesizedExpression())
            return this.visitParenthesizedExpression(ctx.parenthesizedExpression());
        if (ctx.bracketExpression())
            return this.visitBracketExpression(ctx.bracketExpression());
        throw new Error('Invalid primary expression');
    }
    visitAtom(ctx) {
        const raw = ctx.text;
        return {
            type: 'atom',
            atomIndex: Number(raw.slice(1)),
        };
    }
    visitTerm(ctx) {
        const raw = ctx.text;
        return {
            type: 'term',
            termIndex: Number(raw.slice(1)),
        };
    }
    visitNumber(ctx) {
        return {
            type: 'number',
            value: Number(ctx.text),
        };
    }
    visitParenthesizedExpression(ctx) {
        return {
            type: 'group',
            bracketType: 'paren',
            expression: this.visit(ctx.expression()),
        };
    }
    visitBracketExpression(ctx) {
        const expressions = ctx.expression();
        if (expressions.length === 1) {
            return {
                type: 'group',
                bracketType: 'square',
                expression: this.visit(expressions[0]),
            };
        }
        if (expressions.length === 2) {
            return {
                type: 'range',
                min: this.visit(expressions[0]),
                max: this.visit(expressions[1]),
            };
        }
        throw new Error(`Unexpected bracketExpression shape: expected 1 or 2 expressions, got ${expressions.length}`);
    }
}
//# sourceMappingURL=ast-visitor.js.map